import React from 'react';
import { Trophy, Clock, Target, TrendingUp, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface GameHistoryEntry {
    _id: string;
    roomId: string; // [NEW] Added for replay link
    roomCode: string;
    winner: {
        id: string;
        pseudo: string;
    };
    players: Array<{
        id: string;
        pseudo: string;
        lucidity: number;
    }>;
    duration: number; // in seconds
    turnCount: number;
    endedAt: string;
}

interface GameHistoryProps {
    history: GameHistoryEntry[];
    currentUserId: string;
}

const GameHistory: React.FC<GameHistoryProps> = ({ history, currentUserId }) => {
    const navigate = useNavigate();

    const calculateStats = () => {
        if (history.length === 0) return null;

        const totalDuration = history.reduce((sum, game) => sum + game.duration, 0);
        const avgDuration = Math.floor(totalDuration / history.length / 60); // minutes

        const totalTurns = history.reduce((sum, game) => sum + game.turnCount, 0);
        const avgTurns = Math.floor(totalTurns / history.length);

        const wins = history.filter(game => game.winner.id === currentUserId).length;
        const winRate = Math.floor((wins / history.length) * 100);

        return { avgDuration, avgTurns, winRate, wins };
    };

    const stats = calculateStats();

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        if (mins < 60) return `${mins} min`;
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return `${hours}h ${remainingMins}min`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (history.length === 0) {
        return (
            <div className="text-center py-12 text-cream-200/40 border-2 border-dashed border-navy-800 rounded-xl">
                <Trophy size={48} className="mx-auto mb-4 opacity-50" />
                <p>Aucune partie terminée pour le moment.</p>
            </div>
        );
    }

    return (
        <div>
            {/* Stats Overview */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-green-500/10 rounded-full text-green-400 border border-green-500/30">
                            <TrendingUp size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-serif font-bold text-cream-100">{stats.winRate}%</div>
                            <div className="text-xs text-cream-200/70 uppercase tracking-wide">Taux de victoire</div>
                        </div>
                    </div>

                    <div className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-blue-500/10 rounded-full text-blue-400 border border-blue-500/30">
                            <Clock size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-serif font-bold text-cream-100">{stats.avgDuration} min</div>
                            <div className="text-xs text-cream-200/70 uppercase tracking-wide">Temps moyen</div>
                        </div>
                    </div>

                    <div className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 rounded-xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-purple-500/10 rounded-full text-purple-400 border border-purple-500/30">
                            <Target size={24} />
                        </div>
                        <div>
                            <div className="text-2xl font-serif font-bold text-cream-100">{stats.avgTurns}</div>
                            <div className="text-xs text-cream-200/70 uppercase tracking-wide">Coups moyens</div>
                        </div>
                    </div>
                </div>
            )}

            {/* History List */}
            <div className="space-y-3">
                {history.slice(0, 10).map((game, index) => {
                    const isWinner = game.winner.id === currentUserId;
                    return (
                        <motion.div
                            key={game._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`bg-navy-900/40 border rounded-lg p-4 flex items-center justify-between ${isWinner
                                ? 'border-gold-500/50 bg-gold-500/5'
                                : 'border-navy-700'
                                }`}
                        >
                            <div className="flex items-center gap-4">
                                {isWinner && (
                                    <div className="p-2 bg-gold-500/20 rounded-full text-gold-400">
                                        <Trophy size={20} />
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-gold-400 font-bold">{game.roomCode}</span>
                                        <span className={`text-sm ${isWinner ? 'text-gold-300 font-bold' : 'text-cream-200/60'}`}>
                                            {isWinner ? 'Victoire' : `Gagné par ${game.winner.pseudo}`}
                                        </span>
                                    </div>
                                    <div className="text-xs text-cream-200/50 mt-1">
                                        {formatDate(game.endedAt)} • {formatDuration(game.duration)} • {game.turnCount} coups
                                    </div>
                                </div>
                            </div>

                            <div className="text-right flex items-center gap-4">
                                <div className="text-sm text-cream-200/70 hidden sm:block">
                                    {game.players.length} joueurs
                                </div>
                                <button
                                    onClick={() => navigate(`/replay/${game.roomId || game._id}`)}
                                    className="p-2 text-gold-400 hover:bg-gold-500/10 rounded-full transition-colors"
                                    title="Revoir la partie"
                                >
                                    <PlayCircle size={20} />
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {history.length > 10 && (
                <div className="text-center mt-4 text-cream-200/50 text-sm">
                    Affichage des 10 dernières parties sur {history.length}
                </div>
            )}
        </div>
    );
};

export default GameHistory;
